import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useWatch, useFormContext } from 'react-hook-form';
import {
    Loading,
    Error,
    useGetMany,
    useGetList,
    CheckboxGroupInput,
    RadioButtonGroupInput,
    ReferenceArrayInput,
    useResourceContext,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    FormatListBulleted as ListIcon,
    Computer as ComputerIcon,
    Label as TagIcon,
    Place as PinIcon,
} from '@mui/icons-material';

import { format } from 'date-fns';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';

import { StyledSelectArrayInput } from '@src/custom/Styled';

import './ContentTargetingInput.scss';

const STABLE_EMPTY_ARRAY = [];
const STABLE_EMPTY_OBJECT = {};

const PLATFORMS_PARAMS = {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { include: 'products.available_states,available_states' },
};

const STATES_PARAMS = {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
};

const isFutureDate = (date) => {
    if (!date) return false;
    const parsed = new Date(date);
    return !Number.isNaN(parsed.getTime()) && parsed > new Date();
};

const normalizeId = id => (id !== null && id !== undefined ? String(id) : id);
const normalizeIds = ids => {
    if (!Array.isArray(ids)) return STABLE_EMPTY_ARRAY;
    return ids.map(v => (v && typeof v === 'object' ? normalizeId(v.id) : normalizeId(v))).filter(v => v !== null && v !== undefined);
};

const formatPlatformName = (name) => {
    if (name === 'Agent360') return 'Signature (Agent360)';
    if (name === 'ARS') return 'Signature (ARS)';
    if (name === 'Foremost 5.0') return 'Foremost 5.0 (Agent360)';
    return name;
};

const unwrapErrorMessage = (error) => {
    if (!error) return null;
    if (typeof error === 'string') return error;

    let { message } = error;
    while (message && typeof message === 'object' && message.message) {
        ({ message } = message);
    }

    return typeof message === 'string' ? message : null;
};

const getErrorMessage = (errors, source) => unwrapErrorMessage(get(errors, source));

const PlatformGrid = ({
    platforms,
    selectedPlatformIds,
    onPlatformToggle,
    onPlatformModeChange,
    disabled,
}) => (
    <Grid container sx={{ width: '100%' }}>
        {platforms.map(p => {
            const pId = normalizeId(p.id);
            return (
                <Grid key={pId} size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ width: '100%' }}>
                        <CardContent className="platform-grid-card-content">
                            <CheckboxGroupInput
                                source="_dummy_platforms" // We handle selection manually because of side effects
                                choices={[p]}
                                disabled={disabled}
                                helperText={false}
                                label={false}
                                optionText={choice => formatPlatformName(choice.name)}
                                onChange={() => onPlatformToggle(pId)}
                            />
                            {selectedPlatformIds.includes(pId) ? (
                                <RadioButtonGroupInput
                                    row
                                    disabled={disabled}
                                    helperText={false}
                                    label={false}
                                    source={`_platform_modes.${pId}`}
                                    choices={[
                                        { id: 'auto', name: 'Automatic' },
                                        { id: 'include', name: 'Manual (selected)' },
                                        // { id: 'exclude', name: 'Exclude' },
                                    ]}
                                    onChange={(val) => onPlatformModeChange(pId, val)}
                                />
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>
            );
        })}
    </Grid>
);

const ProductGrid = ({
    platforms,
    selectedPlatformIds,
    platformModes,
    productsSource,
    disabled,
}) => (
    <Grid container sx={{ width: '100%' }}>
        {platforms.map(p => {
            const pId = normalizeId(p.id);
            const mode = platformModes[pId] || 'auto';
            const locked = (mode === 'auto' && selectedPlatformIds.includes(pId)) || disabled;

            return (
                <Grid key={pId} size={{ xs: 12, md: 4 }}>
                    <Box className={classNames('product-grid-item', { 'product-label-locked': locked })}>
                        <Typography className="product-grid-title" variant="subtitle2">{formatPlatformName(p.name)}</Typography>
                        <CheckboxGroupInput
                            source={productsSource}
                            choices={p.products || []}
                            helperText={false}
                            label={false}
                            disabled={locked}
                            row={false}
                        />
                    </Box>
                </Grid>
            );
        })}
    </Grid>
);

const StateSection = ({
    isPlatformProductSelected,
    stateMode,
    allStates,
    statesSource,
    derivedStateIds,
    selectedStateIds,
    invalidStateIds,
    allowUnavailableStates,
    onStateModeChange,
    onSelectAll,
    // onSelectAvailable,
    onClearAll,
    onReverse,
    disabled,
}) => {
    const isDisabled = (isPlatformProductSelected && stateMode === 'auto') || disabled;
    const lockToAvailable = isPlatformProductSelected && stateMode === 'include' && !allowUnavailableStates;
    const stateChoices = useMemo(() => allStates.map(state => {
        const sId = normalizeId(state.id);
        return {
            ...state,
            disabled: lockToAvailable && !derivedStateIds.includes(sId) && !selectedStateIds.includes(sId),
        };
    }), [allStates, lockToAvailable, derivedStateIds, selectedStateIds]);

    return (
        <>
            <Typography gutterBottom className="state-section-title" variant="subtitle1">States</Typography>
            {Boolean(isPlatformProductSelected) && (
                <RadioButtonGroupInput
                    row
                    className="state-section-radio-group"
                    disabled={disabled}
                    helperText={false}
                    label={false}
                    source="_state_mode"
                    choices={[
                        { id: 'auto', name: 'Automatic (Derived)' },
                        { id: 'include', name: 'Manual (selected)' },
                        // { id: 'exclude', name: 'Exclude' },
                    ]}
                    onChange={(val) => onStateModeChange(val)}
                />
            )}

            <Box>
                {(stateMode !== 'auto' || !isPlatformProductSelected) && !disabled && (
                    <Box className="state-section-actions">
                        <Button size="small" onClick={onSelectAll}>Select All</Button>
                        { /* <Button size="small" onClick={onSelectAvailable}>Select Available</Button> */ }
                        <Button size="small" onClick={onClearAll}>Clear All</Button>
                        <Button size="small" onClick={onReverse}>Reverse</Button>
                    </Box>
                )}
                {stateMode === 'auto' && isPlatformProductSelected ? null : (
                    <CheckboxGroupInput
                        source={statesSource}
                        choices={stateChoices}
                        helperText={false}
                        label={false}
                        disableValue="disabled"
                        optionText={choice => {
                            const cId = normalizeId(choice.id);
                            return (
                                <Typography
                                    className={classNames('state-label', {
                                        'derived': derivedStateIds.includes(cId) && isPlatformProductSelected && stateMode !== 'auto',
                                        'invalid': invalidStateIds.includes(cId),
                                    })}
                                    variant="body2"
                                    sx={{
                                        color: (choice.disabled || (isDisabled && !derivedStateIds.includes(cId))) ? '#999' : undefined,
                                    }}
                                >
                                    {choice.name}
                                    {invalidStateIds.includes(cId) && ' (Invalid)'}
                                </Typography>
                            );
                        }}
                        disabled={isDisabled}
                        sx={{
                            '& .MuiFormGroup-root': {
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gridAutoFlow: 'column',
                                gridTemplateRows: `repeat(${Math.ceil(stateChoices.length / 5)}, auto)`,
                            }
                        }}
                    />
                )}
            </Box>
        </>
    );
};

const FrontendPreview = ({
    publishedAt,
    categoriesData,
    categoriesChoices,
    selectedCategoryIds,
    categoriesReference,
    platforms,
    selectedPlatformIds,
    platformModes,
    selectedProductIds,
    allStates,
    resolvedStateIds,
    invalidStateIds,
    salesChannelsData,
}) => (
    <Box className="frontend-preview">
        <Typography gutterBottom color="primary" variant="h6">Frontend Preview</Typography>
        <Card className="preview-card">
            <Box className="preview-content">
                {/* Date Row */}
                <Box className="preview-row">
                    <CalendarIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">Date:</Typography>
                    <Typography variant="body2">{publishedAt ? format(new Date(publishedAt), 'MMMM d, yyyy') : 'N/A'}</Typography>
                </Box>

                {/* Categories Row */}
                <Box className="preview-row">
                    <ListIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">Categories:</Typography>
                    <Typography variant="body2">
                        {categoriesData?.map(c => c.name).join(', ') ||
                            (categoriesChoices && selectedCategoryIds.length > 0 ?
                                categoriesChoices.filter(c => selectedCategoryIds.includes(normalizeId(c.id))).map(c => c.name).join(', ') :
                                (selectedCategoryIds.length > 0 && !categoriesReference ? selectedCategoryIds.join(', ') : 'N/A'))}
                    </Typography>
                </Box>

                {/* Platforms Row */}
                <Box className="preview-row">
                    <ComputerIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">Platforms:</Typography>
                    <Typography variant="body2">
                        {platforms.filter(p => selectedPlatformIds.includes(normalizeId(p.id))).map(p => formatPlatformName(p.name)).join(', ') || 'N/A'}
                    </Typography>
                </Box>

                {/* Products Row */}
                <Box className="preview-row">
                    <TagIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">Products:</Typography>
                    <Typography variant="body2">
                        {platforms.flatMap(p => {
                            const pId = normalizeId(p.id);
                            const isPlatformSelected = selectedPlatformIds.includes(pId);

                            if (!isPlatformSelected) {
                                return p.products?.filter(prod => selectedProductIds.includes(normalizeId(prod.id))).map(prod => prod.name) || [];
                            }

                            const mode = platformModes[pId] || 'auto';
                            const platformProductIds = p.products?.map(prod => normalizeId(prod.id)) || [];
                            const activeProductIds = mode === 'auto' ?
                                platformProductIds :
                                intersection(selectedProductIds, platformProductIds);

                            return p.products?.filter(prod => activeProductIds.includes(normalizeId(prod.id))).map(prod => prod.name) || [];
                        }).filter((v, i, a) => a.indexOf(v) === i).join(', ') || 'N/A'}
                    </Typography>
                </Box>

                {/* States Row */}
                <Box className="preview-row">
                    <PinIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">States:</Typography>
                    <Typography className="preview-states-text" variant="body2">
                        {resolvedStateIds.length > 0 ? (
                            allStates.filter(s => resolvedStateIds.includes(normalizeId(s.id)))
                                .sort((a, b) => (a.abbreviation || a.name).localeCompare(b.abbreviation || b.name))
                                .map((s, idx, arr) => (
                                    <React.Fragment key={s.id}>
                                        <span className={classNames({ 'state-invalid': invalidStateIds.includes(normalizeId(s.id)) })}>
                                            {s.abbreviation || s.name}
                                            {invalidStateIds.includes(normalizeId(s.id)) ? ' (Invalid)' : ''}
                                        </span>
                                        {idx < arr.length - 1 ? ', ' : ''}
                                    </React.Fragment>
                                ))
                        ) : 'N/A'}
                    </Typography>
                </Box>

                {/* Sales Channels Row */}
                <Box className="preview-row">
                    <TagIcon className="preview-icon" />
                    <Typography className="preview-label" variant="body2">Sales Channels:</Typography>
                    <Typography variant="body2">
                        {salesChannelsData?.map(sc => sc.name).join(', ') || 'N/A'}
                    </Typography>
                </Box>
            </Box>
        </Card>
    </Box>
);

const parseAndNormalizeIds = (str) => {
    return normalizeIds(JSON.parse(str));
};

const parseAndNormalizeCategoryIds = (str) => {
    const val = JSON.parse(str);
    const arrayVal = Array.isArray(val) ? val : [val];
    return normalizeIds(arrayVal);
};

const normalizeModeValue = (valueOrEvent) => {
    if (typeof valueOrEvent === 'string') {
        return valueOrEvent;
    }

    if (valueOrEvent && typeof valueOrEvent === 'object') {
        const target = valueOrEvent.target || valueOrEvent.currentTarget;
        return target?.value || '';
    }

    return '';
};

const getStatesForPlatform = (p, activeProductIds) => {
    let stateIds = [];
    const fullCoverage = false;

    if (p.use_product_state_availability) {
        p.products?.forEach(prod => {
            const prodId = normalizeId(prod.id);
            if (activeProductIds.includes(prodId)) {
                const prodStates = prod.available_states || prod.states || [];
                if (prodStates.length > 0) {
                    stateIds = union(stateIds, prodStates.map(s => s.slug || normalizeId(s.id)));
                }
            }
        });
    } else {
        const platformStates = p.available_states || p.states || [];
        if (platformStates.length > 0) {
            stateIds = union(stateIds, platformStates.map(s => s.slug || normalizeId(s.id)));
        }
    }

    return { fullCoverage, stateIds };
};

const processSelectedPlatforms = (platforms, selectedPlatformIds, platformModes, selectedProductIds) => {
    let finalStateIds = [];
    let finalFullCoverage = false;

    platforms.forEach(p => {
        const pId = normalizeId(p.id);
        if (!selectedPlatformIds.includes(pId)) return;

        const mode = platformModes[pId] || 'auto';
        const platformProductIds = p.products?.map(prod => normalizeId(prod.id)) || [];
        const activeProductIds = mode === 'auto' ?
            platformProductIds :
            intersection(selectedProductIds, platformProductIds);

        const { fullCoverage, stateIds } = getStatesForPlatform(p, activeProductIds);

        if (fullCoverage) finalFullCoverage = true;
        finalStateIds = union(finalStateIds, stateIds);
    });

    return { finalFullCoverage, finalStateIds };
};

const processSelectedProducts = (platforms, selectedProductIds) => {
    let finalStateIds = [];
    let finalFullCoverage = false;

    platforms.forEach(p => {
        const activeProductIds = p.products?.filter(prod => selectedProductIds.includes(normalizeId(prod.id))).map(prod => normalizeId(prod.id)) || [];

        if (activeProductIds.length > 0) {
            const { fullCoverage, stateIds } = getStatesForPlatform(p, activeProductIds);

            if (fullCoverage) finalFullCoverage = true;
            finalStateIds = union(finalStateIds, stateIds);
        }
    });

    return { finalFullCoverage, finalStateIds };
};

const getStateMode = (manualStateMode, isPlatformProductSelected, selectedStateIds) => {
    if (manualStateMode) return manualStateMode;
    if (isPlatformProductSelected) return 'auto';
    return selectedStateIds.length > 0 ? 'include' : 'auto';
};

const getResolvedStateIds = ({ selectedPlatformIds, selectedProductIds, stateMode, derivedStateIds, selectedStateIds }) => {
    if (selectedPlatformIds.length === 0 && selectedProductIds.length === 0) return selectedStateIds;
    if (stateMode === 'auto') return derivedStateIds;
    if (stateMode === 'include') return selectedStateIds;
    return derivedStateIds.filter(id => !selectedStateIds.includes(id));
};

const getInvalidStateIds = ({
    isPlatformProductSelected,
    stateMode,
    selectedStateIds,
    derivedStateIds,
    allowUnavailableStates,
}) => {
    if (allowUnavailableStates || !isPlatformProductSelected || stateMode !== 'include') return STABLE_EMPTY_ARRAY;
    return selectedStateIds.filter(id => !derivedStateIds.includes(id));
};

const checkAndNormalize = (setValue, source, current, normalized) => {
    if (!source) return;
    const hasObjects = Array.isArray(current) && current.some(v => typeof v === 'object');
    const needsNormalization = !isEqual(current, normalized);

    if (hasObjects || needsNormalization) {
        setValue(source, normalized);
    }
};

const initializePlatformModes = (params) => {
    const { platformsLoading, platforms, selectedPlatformIds, memoizedManualPlatformModes, selectedProductIds, setValue } = params;
    if (platformsLoading || platforms.length === 0) return;

    selectedPlatformIds.forEach(pId => {
        if (memoizedManualPlatformModes[pId]) return;

        const platform = platforms.find(p => normalizeId(p.id) === pId);
        if (!platform) return;

        const platformProductIds = platform.products?.map(p => normalizeId(p.id)) || [];
        const includedProductsOfPlatform = intersection(selectedProductIds, platformProductIds);
        const defaultMode = includedProductsOfPlatform.length === platformProductIds.length ? 'auto' : 'include';

        setValue(`_platform_modes.${pId}`, defaultMode);
    });
};

const syncPlatformsAndProducts = (params) => {
    const {
        platformsLoading,
        platforms,
        selectedPlatformIds,
        selectedProductIds,
        memoizedManualPlatformModes,
        platformsSource,
        setValue,
    } = params;

    if (platformsLoading || platforms.length === 0) return;

    let nextSelectedPlatformIds = selectedPlatformIds;
    let shouldUpdatePlatformsSelection = false;

    platforms.forEach((platform) => {
        const pId = normalizeId(platform.id);
        const platformProductIds = platform.products?.map(product => normalizeId(product.id)) || [];
        if (platformProductIds.length === 0) return;

        const selectedProductsOfPlatform = intersection(selectedProductIds, platformProductIds);
        if (selectedProductsOfPlatform.length === 0) return;

        if (!nextSelectedPlatformIds.includes(pId)) {
            nextSelectedPlatformIds = union(nextSelectedPlatformIds, [pId]);
            shouldUpdatePlatformsSelection = true;
        }

        const shouldBeAutoMode = selectedProductsOfPlatform.length === platformProductIds.length;
        const nextMode = shouldBeAutoMode ? 'auto' : 'include';
        const currentMode = memoizedManualPlatformModes[pId];

        if (currentMode !== nextMode) {
            setValue(`_platform_modes.${pId}`, nextMode);
        }
    });

    if (shouldUpdatePlatformsSelection) {
        setValue(platformsSource, nextSelectedPlatformIds);
    }
};

const handleDefaultStateMode = (params) => {
    const {
        platformsLoading,
        statesLoading,
        isPlatformProductSelected,
        derivedStateIds,
        selectedStateIds,
        allowUnavailableStates,
        manualStateMode,
        statesSource,
        setValue,
    } = params;

    if (platformsLoading || statesLoading) return;
    if (!isPlatformProductSelected) return;
    if (allowUnavailableStates && selectedStateIds.length > 0) {
        if (manualStateMode !== 'include') {
            setValue('_state_mode', 'include');
        }

        return;
    }

    if (derivedStateIds.length === 0) return;
    if (manualStateMode) return;

    const derivedStatesCoveredBySelection = derivedStateIds.every(id => selectedStateIds.includes(id));
    const selectedStatesCoverAllDerived = selectedStateIds.length === derivedStateIds.length && derivedStatesCoveredBySelection;
    const selectedIsSupersetOfDerived = derivedStateIds.length > 0 && derivedStatesCoveredBySelection;
    const defaultStateMode = selectedStateIds.length === 0 ||
        selectedStatesCoverAllDerived ||
        selectedIsSupersetOfDerived ?
        'auto' :
        'include';

    if (manualStateMode !== defaultStateMode) {
        setValue('_state_mode', defaultStateMode);
    }

    if (defaultStateMode === 'auto' && selectedStateIds.length > 0) {
        setValue(statesSource, STABLE_EMPTY_ARRAY);
    }
};

const getWatchName = (source) => source || '_none_';

const getSelectedCategory = (watchedCategoryIds, selectedCategoryIds) => {
    return Array.isArray(watchedCategoryIds) ? selectedCategoryIds : selectedCategoryIds[0];
};

const getTargetingStatus = (platformsLoading, statesLoading, platformsError, statesError) => {
    if (platformsLoading || statesLoading) return 'loading';
    if (platformsError || statesError) return 'error';
    return 'ready';
};

const getTargetingError = (platformsError, statesError) => platformsError || statesError;

const isGetManyEnabled = (reference, ids) => Boolean(reference) && ids.length > 0;

const FormErrorMessage = ({ errors, source }) => {
    const msg = getErrorMessage(errors, source);
    if (!msg) return null;
    return (
        <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
            {msg}
        </Typography>
    );
};

FormErrorMessage.propTypes = {
    errors: PropTypes.object,
    source: PropTypes.string,
};

const SalesChannelsSection = ({ source, reference, resource, disabled }) => {
    if (!source || !reference) return null;
    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Typography gutterBottom className="state-section-title" variant="subtitle1">
                Sales Channels
            </Typography>
            <Grid container sx={{ width: '100%' }}>
                <Grid size={{ xs: 12 }}>
                    <ReferenceArrayInput
                        required
                        label="Sales Channel"
                        reference={reference}
                        source={source}
                        resource={resource}
                        disabled={disabled}
                    >
                        <StyledSelectArrayInput fullWidth disabled={disabled} />
                    </ReferenceArrayInput>
                </Grid>
            </Grid>
        </Box>
    );
};

SalesChannelsSection.propTypes = {
    source: PropTypes.string,
    reference: PropTypes.string,
    resource: PropTypes.string,
};

const ContentTargetingInput = ({
    platformsSource,
    productsSource,
    statesSource,
    statusSource = 'status',
    publishedAtSource,
    categoriesSource,
    salesChannelsSource,
    categoriesReference,
    salesChannelsReference,
    categoriesChoices,
    disabled,
    hideTitle,
}) => {
    const resource = useResourceContext();
    const { setValue, formState: { errors } } = useFormContext();

    // Stable watched values using content-based keys to prevent infinite re-renders
    const watchedPlatformIds = useWatch({ name: getWatchName(platformsSource) });
    const watchedPlatformIdsStr = useMemo(() => JSON.stringify(watchedPlatformIds ?? null), [watchedPlatformIds]);
    const selectedPlatformIds = useMemo(() => parseAndNormalizeIds(watchedPlatformIdsStr), [watchedPlatformIdsStr]);

    const watchedProductIds = useWatch({ name: getWatchName(productsSource) });
    const watchedProductIdsStr = useMemo(() => JSON.stringify(watchedProductIds ?? null), [watchedProductIds]);
    const selectedProductIds = useMemo(() => parseAndNormalizeIds(watchedProductIdsStr), [watchedProductIdsStr]);

    const watchedStateIds = useWatch({ name: getWatchName(statesSource) });
    const watchedStateIdsStr = useMemo(() => JSON.stringify(watchedStateIds ?? null), [watchedStateIds]);
    const selectedStateIds = useMemo(() => parseAndNormalizeIds(watchedStateIdsStr), [watchedStateIdsStr]);

    const statusValue = useWatch({ name: getWatchName(statusSource) });
    const publishedAt = useWatch({ name: getWatchName(publishedAtSource) });
    const allowUnavailableStates = statusValue === 'draft' || isFutureDate(publishedAt);

    const watchedCategoryIds = useWatch({ name: getWatchName(categoriesSource) });
    const watchedCategoryIdsStr = useMemo(() => JSON.stringify(watchedCategoryIds ?? null), [watchedCategoryIds]);
    const selectedCategoryIds = useMemo(() => parseAndNormalizeCategoryIds(watchedCategoryIdsStr), [watchedCategoryIdsStr]);

    const watchedSalesChannelIds = useWatch({ name: getWatchName(salesChannelsSource) });
    const watchedSalesChannelIdsStr = useMemo(() => JSON.stringify(watchedSalesChannelIds ?? null), [watchedSalesChannelIds]);
    const selectedSalesChannelIds = useMemo(() => parseAndNormalizeIds(watchedSalesChannelIdsStr), [watchedSalesChannelIdsStr]);

    // Normalize form state if it contains objects or non-string IDs
    useEffect(() => {
        checkAndNormalize(setValue, platformsSource, watchedPlatformIds, selectedPlatformIds);
        checkAndNormalize(setValue, productsSource, watchedProductIds, selectedProductIds);
        checkAndNormalize(setValue, statesSource, watchedStateIds, selectedStateIds);
        checkAndNormalize(setValue, categoriesSource, watchedCategoryIds, getSelectedCategory(watchedCategoryIds, selectedCategoryIds));
        checkAndNormalize(setValue, salesChannelsSource, watchedSalesChannelIds, selectedSalesChannelIds);
    }, [
        watchedPlatformIds, watchedProductIds, watchedStateIds, watchedCategoryIds, watchedSalesChannelIds,
        selectedPlatformIds, selectedProductIds, selectedStateIds, selectedCategoryIds, selectedSalesChannelIds,
        platformsSource, productsSource, statesSource, categoriesSource, salesChannelsSource,
        setValue
    ]);

    // Sync dummy platforms for the checkboxes
    useEffect(() => {
        setValue('_dummy_platforms', selectedPlatformIds);
    }, [selectedPlatformIds, setValue]);

    // Manual overrides for modes in form state
    const watchedManualPlatformModes = useWatch({ name: '_platform_modes' });
    const memoizedManualPlatformModes = useMemo(() => watchedManualPlatformModes || STABLE_EMPTY_OBJECT, [watchedManualPlatformModes]);
    const manualStateMode = useWatch({ name: '_state_mode' });

    // Fetch data using useGetList
    const { data: platforms = STABLE_EMPTY_ARRAY, isPending: platformsLoading, error: platformsError } = useGetList('platforms', PLATFORMS_PARAMS);
    const { data: allStates = STABLE_EMPTY_ARRAY, isPending: statesLoading, error: statesError } = useGetList('states', STATES_PARAMS);

    // Derived modes
    const platformModes = useMemo(() => {
        const modes = { ...memoizedManualPlatformModes };
        if (platformsLoading || platforms.length === 0) return modes;

        selectedPlatformIds.forEach(pId => {
            if (modes[pId]) return;

            const platform = platforms.find(p => normalizeId(p.id) === pId);
            if (!platform) return;

            const platformProductIds = platform.products?.map(p => normalizeId(p.id)) || [];
            const includedProductsOfPlatform = intersection(selectedProductIds, platformProductIds);

            if (includedProductsOfPlatform.length === platformProductIds.length) {
                modes[pId] = 'auto';
            } else {
                modes[pId] = 'include';
            }
        });

        return modes;
    }, [platformsLoading, platforms, selectedPlatformIds, selectedProductIds, memoizedManualPlatformModes]);

    useEffect(() => {
        initializePlatformModes({
            platformsLoading,
            platforms,
            selectedPlatformIds,
            memoizedManualPlatformModes,
            selectedProductIds,
            setValue,
        });
    }, [
        platformsLoading,
        platforms,
        selectedPlatformIds,
        selectedProductIds,
        memoizedManualPlatformModes,
        setValue,
    ]);

    // Keep platform selection/modes in sync when products are selected directly.
    useEffect(() => {
        syncPlatformsAndProducts({
            platformsLoading,
            platforms,
            selectedPlatformIds,
            selectedProductIds,
            memoizedManualPlatformModes,
            platformsSource,
            setValue,
        });
    }, [
        platformsLoading,
        platforms,
        selectedPlatformIds,
        selectedProductIds,
        memoizedManualPlatformModes,
        platformsSource,
        setValue,
    ]);

    const isPlatformProductSelected = selectedPlatformIds.length > 0 || selectedProductIds.length > 0;
    const stateMode = getStateMode(manualStateMode, isPlatformProductSelected, selectedStateIds);

    // Fetch categories and sales channels for preview
    const categoriesManyParams = useMemo(() => ({ ids: selectedCategoryIds }), [selectedCategoryIds]);
    const { data: categoriesData } = useGetMany(
        categoriesReference,
        categoriesManyParams,
        { enabled: isGetManyEnabled(categoriesReference, selectedCategoryIds) },
    );

    const salesChannelsManyParams = useMemo(() => ({ ids: selectedSalesChannelIds }), [selectedSalesChannelIds]);
    const { data: salesChannelsData } = useGetMany(
        salesChannelsReference,
        salesChannelsManyParams,
        { enabled: isGetManyEnabled(salesChannelsReference, selectedSalesChannelIds) },
    );

    // Logic for platform toggle
    const handlePlatformToggle = (pId) => {
        const normalizedPId = normalizeId(pId);
        const isSelected = selectedPlatformIds.includes(normalizedPId);
        let newSelection;

        if (isSelected) {
            newSelection = selectedPlatformIds.filter(id => id !== normalizedPId);
            // Remove products associated with this platform
            const platform = platforms.find(p => normalizeId(p.id) === normalizedPId);
            const pProdIds = platform.products?.map(p => normalizeId(p.id)) || [];

            setValue(productsSource, selectedProductIds.filter(id => !pProdIds.includes(id)));
        } else {
            newSelection = [...selectedPlatformIds, normalizedPId];
            setValue(`_platform_modes.${normalizedPId}`, 'auto');
            // In 'auto' mode, all products are added
            const platform = platforms.find(p => normalizeId(p.id) === normalizedPId);
            const pProdIds = platform.products?.map(p => normalizeId(p.id)) || [];

            setValue(productsSource, union(selectedProductIds, pProdIds));
        }

        setValue(platformsSource, newSelection);
    };

    const handlePlatformModeChange = (pId, modeOrEvent) => {
        const mode = normalizeModeValue(modeOrEvent);
        if (!mode) return;

        const normalizedPId = normalizeId(pId);
        // RadioButtonGroupInput already updates the field via source, but we keep this for side effects.
        // We only call setValue if it's different from the value being set by the input to avoid loops.
        if (memoizedManualPlatformModes[normalizedPId] !== mode) {
            setValue(`_platform_modes.${normalizedPId}`, mode);
        }

        const platform = platforms.find(p => normalizeId(p.id) === normalizedPId);
        const pProdIds = platform.products?.map(p => normalizeId(p.id)) || [];

        if (mode === 'auto') {
            setValue(productsSource, union(selectedProductIds, pProdIds));
        } else {
            // In include mode, start with no selected products for this platform.
            setValue(productsSource, selectedProductIds.filter(id => !pProdIds.includes(id)));
        }
    };

    // Derived states based on selected platforms and products
    const derivedStateIds = useMemo(() => {
        if (platformsLoading || statesLoading) return STABLE_EMPTY_ARRAY;
        if (selectedPlatformIds.length === 0 && selectedProductIds.length === 0) return allStates.map(s => normalizeId(s.id));

        const { finalFullCoverage, finalStateIds } = selectedPlatformIds.length === 0 ?
            processSelectedProducts(platforms, selectedProductIds) :
            processSelectedPlatforms(platforms, selectedPlatformIds, platformModes, selectedProductIds);

        if (finalFullCoverage) return allStates.map(s => normalizeId(s.id));

        return finalStateIds;
    }, [platformsLoading, statesLoading, allStates, selectedPlatformIds, selectedProductIds, platforms, platformModes]);

    // Final resolved states for preview
    const resolvedStateIds = useMemo(() => {
        return getResolvedStateIds({
            selectedPlatformIds,
            selectedProductIds,
            stateMode,
            derivedStateIds,
            selectedStateIds,
        });
    }, [selectedPlatformIds, selectedProductIds, stateMode, derivedStateIds, selectedStateIds]);

    const invalidStateIds = useMemo(() => {
        return getInvalidStateIds({
            isPlatformProductSelected,
            stateMode,
            selectedStateIds,
            derivedStateIds,
            allowUnavailableStates,
        });
    }, [isPlatformProductSelected, stateMode, selectedStateIds, derivedStateIds, allowUnavailableStates]);

    useEffect(() => {
        handleDefaultStateMode({
            platformsLoading,
            statesLoading,
            isPlatformProductSelected,
            derivedStateIds,
            selectedStateIds,
            allowUnavailableStates,
            manualStateMode,
            statesSource,
            setValue,
        });
    }, [
        platformsLoading,
        statesLoading,
        isPlatformProductSelected,
        derivedStateIds,
        selectedStateIds,
        allowUnavailableStates,
        manualStateMode,
        statesSource,
        setValue,
    ]);

    const handleStateModeChange = (modeOrEvent) => {
        const mode = normalizeModeValue(modeOrEvent);
        if (!mode) return;

        setValue('_state_mode', mode);
        if (mode === 'auto') {
            setValue(statesSource, STABLE_EMPTY_ARRAY);
        } else if (mode === 'include') {
            setValue(statesSource, derivedStateIds);
        } else if (mode === 'exclude') {
            setValue(statesSource, STABLE_EMPTY_ARRAY);
        }
    };

    const status = getTargetingStatus(platformsLoading, statesLoading, platformsError, statesError);
    if (status === 'loading') return <Loading />;
    if (status === 'error') return <Error error={getTargetingError(platformsError, statesError)} />;

    return (
        <Box className="content-targeting-input">
            {!hideTitle && (
                <Typography gutterBottom variant="h6">Content Targeting</Typography>
            )}

            <Typography gutterBottom variant="subtitle1">Platforms</Typography>
            <FormErrorMessage errors={errors} source={platformsSource} />
            <PlatformGrid
                platforms={platforms}
                selectedPlatformIds={selectedPlatformIds}
                disabled={disabled}
                onPlatformModeChange={handlePlatformModeChange}
                onPlatformToggle={handlePlatformToggle}
            />

            <Typography gutterBottom className="state-section-title" variant="subtitle1">Products</Typography>
            <FormErrorMessage errors={errors} source={productsSource} />
            <ProductGrid
                platformModes={platformModes}
                platforms={platforms}
                productsSource={productsSource}
                selectedPlatformIds={selectedPlatformIds}
                disabled={disabled}
            />

            <FormErrorMessage errors={errors} source={statesSource} />
            {invalidStateIds.length > 0 ? (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                    Some selected states are not available for the selected product/platform.
                </Typography>
            ) : null}
            <StateSection
                allStates={allStates}
                allowUnavailableStates={allowUnavailableStates}
                derivedStateIds={derivedStateIds}
                invalidStateIds={invalidStateIds}
                isPlatformProductSelected={isPlatformProductSelected}
                selectedStateIds={selectedStateIds}
                stateMode={stateMode}
                statesSource={statesSource}
                disabled={disabled}
                onClearAll={() => setValue(statesSource, STABLE_EMPTY_ARRAY)}
                onSelectAll={() => setValue(
                    statesSource,
                    isPlatformProductSelected && stateMode === 'include' && !allowUnavailableStates ?
                        derivedStateIds :
                        allStates.map(s => normalizeId(s.id)),
                )}
                // onSelectAvailable={() => setValue(statesSource, derivedStateIds)}
                onStateModeChange={handleStateModeChange}
                onReverse={() => {
                    const statesToReverse = isPlatformProductSelected && stateMode === 'include' && !allowUnavailableStates ?
                        derivedStateIds :
                        allStates.map(s => normalizeId(s.id));
                    const nextSelection = statesToReverse.filter(id => !selectedStateIds.includes(id));
                    setValue(statesSource, nextSelection);
                }}
            />

            <SalesChannelsSection
                reference={salesChannelsReference}
                resource={resource}
                source={salesChannelsSource}
                disabled={disabled}
            />

            <FrontendPreview
                allStates={allStates}
                categoriesChoices={categoriesChoices}
                categoriesData={categoriesData}
                categoriesReference={categoriesReference}
                invalidStateIds={invalidStateIds}
                platformModes={platformModes}
                platforms={platforms}
                publishedAt={publishedAt}
                resolvedStateIds={resolvedStateIds}
                salesChannelsData={salesChannelsData}
                selectedCategoryIds={selectedCategoryIds}
                selectedPlatformIds={selectedPlatformIds}
                selectedProductIds={selectedProductIds}
            />
        </Box>
    );
};

ContentTargetingInput.propTypes = {
    platformsSource: PropTypes.string.isRequired,
    productsSource: PropTypes.string.isRequired,
    statesSource: PropTypes.string.isRequired,
    statusSource: PropTypes.string,
    publishedAtSource: PropTypes.string,
    categoriesSource: PropTypes.string,
    salesChannelsSource: PropTypes.string,
    categoriesReference: PropTypes.string,
    salesChannelsReference: PropTypes.string,
    categoriesChoices: PropTypes.arrayOf(PropTypes.object),
    disabled: PropTypes.bool,
    hideTitle: PropTypes.bool,
};

export default ContentTargetingInput;
